/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect } from 'react';
import { useSEOHead } from '../../hooks/useSEOHead';
import { CarsUnder30kHero } from './components/CarsUnder30kHero';
import { ValueProposition } from './components/ValueProposition';
import { FeaturedVehicles } from './components/FeaturedVehicles';
import { FinancingOptions } from './components/FinancingOptions';
import { BuyerGuide } from './components/BuyerGuide';
import { CarsUnder30kFaq } from './components/CarsUnder30kFaq';
import { CarsUnder30kCta } from './components/CarsUnder30kCta';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import { pageFaqs } from '../../mocks/pageFaqs';

const OG_IMAGE_30K = 'https://public.readdy.ai/ai/img_res/0e3ab648e79f986cff8e4d05d53e6973.jpg';

export default function CarsUnder30kPage() {
  useSEOHead({
    title: 'Cars Under $30K Oregon Coast | Warrenton | Rockhound',
    description: 'Quality used cars under $30K on the Oregon Coast & SW Washington. Sedans, SUVs, trucks & AWD vehicles for Warrenton, Astoria, Seaside, Tillamook, Lincoln City, Newport.',
    keywords: 'cars under 30000 Oregon Coast, used cars under 30k Astoria, affordable SUV Warrenton Oregon, used trucks under 30000 Oregon, mid-range used cars Seaside, vehicles under 30k Tillamook, used cars Lincoln City, Newport Oregon used cars, SW Washington used cars under 30000, Long Beach WA used cars, Ilwaco used vehicles, Rockhound Auto',
    canonicalUrl: 'https://rockhoundauto.com/cars-under-30k',
    ogImage: OG_IMAGE_30K,
    geo: {
      region: 'US-OR',
      placename: 'Warrenton, Oregon',
      position: '46.1651;-123.9237',
    },
  });

  useEffect(() => {
    const BASE = 'https://rockhoundauto.com';

    injectJsonLd('schema-under-30k-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE },
        { '@type': 'ListItem', position: 2, name: 'Inventory', item: `${BASE}/inventory` },
        { '@type': 'ListItem', position: 3, name: 'Cars Under $30,000', item: `${BASE}/cars-under-30k` },
      ],
    });

    injectJsonLd('schema-cars-under-30k-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Cars Under $30,000 Oregon Coast | Rockhound Auto',
      description: 'Quality used cars under $30,000 on the Oregon Coast & SW Washington. Sedans, SUVs, trucks & AWD vehicles for Warrenton, Astoria, Seaside, Tillamook, Lincoln City, Newport & more.',
      url: `${BASE}/cars-under-30k`,
      isPartOf: { '@type': 'WebSite', name: 'Rockhound Auto', url: BASE },
      breadcrumb: { '@id': `${BASE}/cars-under-30k#breadcrumb` },
    });

    injectJsonLd('schema-cars-under-30k-itemlist', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Used Cars Under $30,000 — Oregon Coast',
      description: 'A curated selection of quality used vehicles priced under $30,000 available at Rockhound Auto in Warrenton, OR.',
      url: `${BASE}/cars-under-30k`,
      numberOfItems: 6,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '2020 Toyota RAV4 — Under $30,000', url: `${BASE}/inventory` },
        { '@type': 'ListItem', position: 2, name: '2019 Honda CR-V AWD — Under $30,000', url: `${BASE}/inventory` },
        { '@type': 'ListItem', position: 3, name: '2020 Ford F-150 — Under $30,000', url: `${BASE}/inventory` },
        { '@type': 'ListItem', position: 4, name: '2021 Subaru Outback AWD — Under $30,000', url: `${BASE}/inventory` },
        { '@type': 'ListItem', position: 5, name: '2019 Chevrolet Silverado — Under $30,000', url: `${BASE}/inventory` },
        { '@type': 'ListItem', position: 6, name: '2020 Nissan Pathfinder — Under $30,000', url: `${BASE}/inventory` },
      ],
    });

    injectFAQSchema(
      'schema-cars-under-30k-faq',
      pageFaqs.carsUnder30k,
      'https://rockhoundauto.com/cars-under-30k#faqpage'
    );

    return () => {
      removeJsonLd('schema-under-30k-breadcrumb');
      removeJsonLd('schema-cars-under-30k-webpage');
      removeJsonLd('schema-cars-under-30k-itemlist');
      removeFAQSchema('schema-cars-under-30k-faq');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Cars Under $30k' }]} />
      <CarsUnder30kHero />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Expert Perspective Block */}
        <div className="mb-16 bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                <i className="ri-car-line text-2xl text-white"></i>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Expert Perspective</h3>
              <p className="text-gray-700 leading-relaxed">
                The $20,000-$30,000 range offers the best value in used cars—recent models with modern safety features and remaining warranty coverage. You avoid new car depreciation but still get reliable transportation. This price point attracts serious buyers who've done their research and know exactly what they want.
              </p>
            </div>
          </div>
        </div>

        <ValueProposition />
        <FeaturedVehicles />
        <BuyerGuide />
        <FinancingOptions />
        <CarsUnder30kFaq />
        <CarsUnder30kCta />
      </div>
    </>
  );
}